/* Isolated product delivery timeline; no cart, pricing or native-picker hooks. */
export function advanceWorkingDays(anchor, days, { skipWeekends = true, excludedDates = [] } = {}) {
  const date = new Date(anchor);
  date.setHours(12, 0, 0, 0);
  const excluded = new Set(excludedDates);
  const key = d => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
  const working = d => !(skipWeekends && [0, 6].includes(d.getDay())) && !excluded.has(key(d));
  let remaining = Math.max(0, Math.trunc(Number(days) || 0));
  let iterations = 0;
  while ((remaining > 0 || !working(date)) && iterations < 370) {
    date.setDate(date.getDate() + 1);
    if (working(date) && remaining > 0) remaining--;
    iterations++;
  }
  return remaining > 0 || !working(date) ? null : date;
}
export function calculateTimeline(config, now = new Date()) {
  const calendar = { skipWeekends: config.skipWeekends, excludedDates: config.excludedDates };
  const ordered = new Date(now);
  ordered.setHours(12, 0, 0, 0);
  const dispatchDays = Math.max(0, Number(config.dispatchDays));
  const firstDispatch = advanceWorkingDays(ordered, Math.max(0, dispatchDays - Number(config.earlyDays || 0)), calendar);
  const lastDispatch = advanceWorkingDays(ordered, dispatchDays, calendar);
  if (!firstDispatch || !lastDispatch) return null;
  const firstDelivery = advanceWorkingDays(firstDispatch, Number(config.transitMin), calendar);
  const lastDelivery = advanceWorkingDays(lastDispatch, Math.max(Number(config.transitMin), Number(config.transitMax)), calendar);
  if (!firstDelivery || !lastDelivery) return null;
  return { ordered, firstDispatch, lastDispatch, firstDelivery, lastDelivery };
}
if (typeof customElements !== 'undefined' && !customElements.get('dippy-delivery-timeline')) {
  customElements.define('dippy-delivery-timeline', class extends HTMLElement {
    connectedCallback() {
      this.refresh();
      this.setupInformation();
      this.refreshTimer = window.setInterval(() => this.refresh(), 60000);
    }
    disconnectedCallback() {
      window.clearInterval(this.refreshTimer);
      window.clearTimeout(this.closeTimer);
      this.informationEvents?.abort();
      this.closeInformation();
    }
    setupInformation() {
      this.informationEvents?.abort();
      this.informationEvents = new AbortController();
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.informationEvents.signal });
      for (const trigger of this.parentElement.querySelectorAll('[data-timeline-info]')) {
        const tooltip = document.getElementById(trigger.getAttribute('aria-describedby'));
        if (!tooltip) continue;
        listen(trigger, 'pointerenter', event => { if (event.pointerType !== 'touch') this.openInformation(trigger, tooltip); });
        listen(trigger, 'pointerleave', () => this.deferClose());
        listen(trigger, 'focus', () => this.openInformation(trigger, tooltip));
        listen(trigger, 'blur', () => this.closeInformation());
        listen(trigger, 'click', () => {
          if (this.activeTrigger === trigger && this.pinnedInformation) this.closeInformation();
          else { this.openInformation(trigger, tooltip); this.pinnedInformation = true; }
        });
        listen(tooltip, 'pointerenter', () => window.clearTimeout(this.closeTimer));
        listen(tooltip, 'pointerleave', () => this.deferClose());
      }
      listen(document, 'pointerdown', event => {
        if (this.activeTrigger && !this.activeTrigger.contains(event.target) && !this.activeTooltip.contains(event.target)) this.closeInformation();
      });
      listen(document, 'keydown', event => { if (event.key === 'Escape') this.closeInformation(); });
      listen(window, 'resize', () => this.positionInformation());
      listen(window, 'scroll', () => this.positionInformation());
    }
    deferClose() {
      window.clearTimeout(this.closeTimer);
      if (!this.pinnedInformation && document.activeElement !== this.activeTrigger) this.closeTimer = window.setTimeout(() => this.closeInformation(), 150);
    }
    openInformation(trigger, tooltip) {
      window.clearTimeout(this.closeTimer);
      if (this.activeTrigger !== trigger) this.closeInformation();
      this.activeTrigger = trigger;
      this.activeTooltip = tooltip;
      trigger.setAttribute('aria-expanded', 'true');
      if (!tooltip.matches(':popover-open')) tooltip.showPopover();
      this.positionInformation();
    }
    closeInformation() {
      if (this.activeTooltip?.matches(':popover-open')) this.activeTooltip.hidePopover();
      this.activeTrigger?.setAttribute('aria-expanded', 'false');
      this.activeTrigger = this.activeTooltip = null;
      this.pinnedInformation = false;
    }
    positionInformation() {
      if (!this.activeTooltip) return;
      const anchor = this.activeTrigger.getBoundingClientRect();
      if (anchor.bottom < 0 || anchor.top > window.innerHeight) { this.closeInformation(); return; }
      const tooltip = this.activeTooltip;
      const bounds = tooltip.getBoundingClientRect();
      const gap = 9;
      const below = anchor.bottom + gap + bounds.height <= window.innerHeight - 12 || anchor.top - gap - bounds.height < 12;
      const left = Math.max(12, Math.min(anchor.left, window.innerWidth - bounds.width - 12));
      const top = below ? anchor.bottom + gap : anchor.top - bounds.height - gap;
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${Math.max(12, Math.min(top, window.innerHeight - bounds.height - 12))}px`;
      tooltip.style.setProperty('--tooltip-arrow', `${Math.max(10, Math.min(anchor.left + anchor.width / 2 - left, bounds.width - 10))}px`);
      tooltip.dataset.placement = below ? 'below' : 'above';
    }
    refresh() {
      const result = calculateTimeline({
        dispatchDays: this.dataset.dispatchDays,
        earlyDays: this.dataset.earlyDays,
        transitMin: this.dataset.transitMin,
        transitMax: this.dataset.transitMax,
        skipWeekends: this.dataset.skipWeekends === 'true',
        excludedDates: (this.dataset.excludedDates || '').split(/\r?\n/).map(value => value.trim())
      });
      if (!result) return;
      const key = date => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      for (const time of this.querySelectorAll('[data-timeline-date]')) {
        const date = result[time.dataset.timelineDate];
        time.dateTime = key(date);
        time.textContent = formatter.format(date);
      }
      for (const range of this.querySelectorAll('[data-timeline-range]')) {
        const dates = range.querySelectorAll('time');
        const same = dates[0].dateTime === dates[1].dateTime;
        dates[1].hidden = same;
        range.querySelector('[data-range-separator]').hidden = same;
      }
      for (const tooltip of this.querySelectorAll('[data-tooltip-template]')) {
        const key = tooltip.dataset.tooltipDate === 'shipped' ? 'Dispatch' : 'Delivery';
        const first = formatter.format(result[`first${key}`]);
        const last = formatter.format(result[`last${key}`]);
        tooltip.textContent = tooltip.dataset.tooltipTemplate.replaceAll('[date]', first === last ? first : `${first} – ${last}`);
      }
      this.positionInformation();
    }
  });
}
